import type { CheckoutSdk } from '@athena-lib/Checkout/utils';
import { createCheckoutInstance, type CountriesResponse } from '@stickerapp-org/checkout-sdk';
import type { TranslationPairs } from '@athena-lib/ProductWizard/types';
import { Type } from '@sinclair/typebox';
import { setGlobalError } from '../components/GlobalSdkError.svelte';
import type { ExpandedCheckoutSettings } from './server/expanders/checkoutSettingsExpander';
import { post } from './http';
import {
  getCartEventsTracker,
  reportInvalidGiftCardOrPromoCode,
} from '$lib/gtmEventHelper.svelte.js';
import { goto } from '$app/navigation';

export const checkoutIdCookieKey = 'checkout-cart-id';
export const checkoutVersionCookieKey = 'checkout-cart-version';

let checkout = $state<{
  sdk: null | CheckoutSdk;
  settings?: ExpandedCheckoutSettings;
  suggestedPostalCode?: string;
  countries?: CountriesResponse;
  translators?: {
    translateLaminate: (product: string) => string;
    translateMaterial: (material: string) => string;
    translateProductName: (productName: string) => string;
  };
}>({ sdk: null });

/**
  Gets the initialized instance of checkout sdk, should only be @null if checkoutSdk has
  not been initialized.
 */
export const getCheckoutSdk = () => checkout?.sdk;
export const setCheckoutState = (newCheckout: typeof checkout) => {
  checkout = newCheckout;
  const translationPairs = newCheckout.settings?.translationPairs;
  if (translationPairs) {
    checkout.translators = {
      translateMaterial: createMaterialTranslator(translationPairs),
      translateLaminate: createLaminateTranslator(translationPairs),
      translateProductName: createProductNameTranslator(translationPairs),
    };
  }

  let countriesResult: CountriesResponse | undefined;
  newCheckout.sdk?.countries().then((x) => {
    countriesResult = x;
  });
  checkout = {
    ...checkout,
    get countries() {
      return countriesResult;
    },
  };
};

export const getCheckoutCountries = () => checkout.countries;

export const createMaterialTranslator =
  (translationPairs: TranslationPairs) => (material: string) => {
    const key = `products_material_${material}`;
    return translationPairs?.[key] ?? key;
  };

export const createLaminateTranslator =
  (translationPairs: TranslationPairs) => (laminate: string) => {
    const key = `products_laminate_${laminate}`;
    return translationPairs?.[key] ?? key;
  };

export const createProductNameTranslator =
  (translationPairs: TranslationPairs) => (productName: string) => {
    const key = `products_product_name_${productName}`;
    return translationPairs?.[key] ?? key;
  };

export const getProductsTranslators = () => checkout.translators;

export const getSuggestedPostalCode = () => {
  const sdkPostCode = checkout.sdk?.state.customer?.address?.postalCode;
  return sdkPostCode || checkout.suggestedPostalCode;
};

export const setSuggestedPostalCode = (postalCode: string) => {
  checkout = { ...checkout, suggestedPostalCode: postalCode };
};

export const getCheckoutSettings = () => checkout.settings;

const saveUserCountry = (countryCode: string) => {
  return post('/api/set_user_country', { countryCode }, Type.Unknown());
};

export const initializeCheckoutSdk = ({
  siteUrl,
  orderConfirmationPageUrl,
  checkoutApiBaseUrl,
  checkoutPageUrl,
  userCountryCode,
  currency,
  language,
  jwt,
  atlasSessionId,
}: {
  checkoutPageUrl: string;
  checkoutApiBaseUrl: string;
  orderConfirmationPageUrl: string;
  userCountryCode: string;
  currency: string;
  language: string;
  siteUrl: string;
  jwt: string;
  atlasSessionId: string | undefined;
}) => {
  const cartId = localStorage.getItem(checkoutIdCookieKey) ?? undefined;
  const cartVersion = localStorage.getItem(checkoutVersionCookieKey) ?? undefined;
  let countryCode = userCountryCode;

  const cartInstance = createCheckoutInstance({
    cartId,
    cartVersion,
    checkoutApiBaseUrl,
    countryCode: countryCode.toLowerCase(),
    currency,
    language,
    siteUrl,
    checkoutPageUrl,
    orderConfirmationPageUrl,
    tokenString: jwt,
    onShowOrderConfirmation: (orderId) => {
      return goto(`${orderConfirmationPageUrl}?orderId=${encodeURIComponent(orderId)}`);
    },
    onUnexpectedError: setGlobalError,
    atlasSessionId: atlasSessionId ?? '',
  });
  const originalAddCode = cartInstance.actions.addCode;
  const cartEventsTracker = getCartEventsTracker();

  const oldCompletePurchase = cartInstance.actions.completePurchase;
  cartInstance.actions.completePurchase = async (...args) => {
    const onPurchase = await cartEventsTracker.purchase().catch(() => (_orderId?: string) => {});
    const result = await oldCompletePurchase(...args);
    const id = 'orderId' in result ? result.orderId : undefined;
    onPurchase(id);
    return result;
  };

  cartInstance.actions.addCode = (...args) => {
    return originalAddCode(...args).then((x) => {
      if (!x.success) {
        reportInvalidGiftCardOrPromoCode(
          cartInstance.state.customer.company?.isCompany ? 'business' : 'private',
        );
      }
      return x;
    });
  };

  cartInstance.state.onChange((newState) => {
    if (countryCode !== newState.countryCode) {
      countryCode = newState.countryCode;
      saveUserCountry(countryCode);
    }
    cartEventsTracker.reportUpdatedLineItems(newState.lines);
    Object.assign(checkout.sdk ?? cartInstance, cartInstance);

    localStorage.setItem(checkoutIdCookieKey, newState.id);
    localStorage.setItem(checkoutVersionCookieKey, newState.version);
  });
  return cartInstance;
};

type GetCustomSticker = CheckoutSdk['getCustomSticker'];
export const invalidateCustomStickerCacheKey = (id: string) => {
  stickerCache.delete(id);
};
const stickerCache = $state<Map<string, ReturnType<GetCustomSticker>>>(new Map());
export const getCustomStickerCached: GetCustomSticker = (id: string) => {
  const sdk = getCheckoutSdk();
  if (!sdk) {
    return Promise.reject('No SDK initialized');
  }
  const cached = stickerCache.get(id);
  if (cached) {
    return cached;
  }
  const freshValue = sdk.getCustomSticker(id);
  stickerCache.set(id, freshValue);
  return freshValue;
};
